/**
 * Escopo da requisicao de consolidacao: grupo familiar, cliente do profissional ou o proprio
 * cliente logado.
 *
 * Fonte UNICA. Toda classe de API que chama a rota de consolidacao resolve por aqui — a URL, o
 * `is_client` do payload e a chave de cache saem juntos, porque desalinhar os tres e o que faz a
 * tela servir o dado de um escopo com o cache de outro. A chave sai PRONTA (`scopeKey`): montar a
 * partir de `allowRequestWithoutClient` colapsa os grupos na constante do cliente.
 *
 * ★ Nasceu depois de um bug medido (AZ-b449ba53): a troca de escopo foi implementada em UMA classe
 * (`apiConsolidatorWalletAnalysis`) e declarada como "as views servem o grupo sem alteracao".
 * Medicao posterior: 1 de 44 classes de API do modulo conhecia o grupo. A coluna Rentabilidade da
 * Carteira atual vinha VAZIA no contexto de grupo porque `apiConsolidatorStatisticsPerAsset`
 * continuava chamando a rota por cliente. Com o `if` copiado, o proximo consumidor repete o erro.
 */

import { carteiraGrupoFamiliar } from '@modules/consolidator/constants/URL'
import { minhaCarteira } from '@constants/URL'
import {
  buildGroupScopeKey,
  resolveScopeKey,
} from '@modules/consolidator/utils/Api/Api'
import store from '@state/store'

/**
 * @returns {Object|null} o grupo familiar ativo, ou `null` no contexto de cliente
 */
export const currentFamilyGroup = () =>
  store.getters['consolidator/currentFamilyGroup'] || null

/**
 * @param {Object} params
 * @param {Boolean} params.isClient o usuario logado e o proprio cliente final
 * @param {Number|String} params.idClienteApp cliente selecionado pelo profissional
 * @param {Object} [params.grupo] grupo ativo; omitido, e lido da store
 * @returns {Object} `{ url, allowRequestWithoutClient, scopeId, scopeKey, isGroup }` — os cinco
 * derivados do MESMO escopo. `allowRequestWithoutClient` autoriza a requisicao a sair sem
 * `id_cliente_app` (guarda de `serializeWalletAnalysisParams`); `scopeKey` e a chave de cache e o
 * carimbo do dado na store, e NUNCA colapsa dois escopos diferentes na mesma string
 */
export const resolveConsolidationScope = ({
  isClient,
  idClienteApp,
  grupo,
} = {}) => {
  const grupoAtivo = grupo === undefined ? currentFamilyGroup() : grupo

  if (grupoAtivo) {
    const scopeId = buildGroupScopeKey(grupoAtivo.id)

    return {
      url: carteiraGrupoFamiliar.consolidation({ id: grupoAtivo.id }),
      allowRequestWithoutClient: true,
      scopeId,
      scopeKey: scopeId,
      isGroup: true,
    }
  }

  return {
    url: isClient
      ? minhaCarteira.consolidationClient
      : minhaCarteira.consolidation({ id: idClienteApp }),
    allowRequestWithoutClient: !!isClient,
    scopeId: idClienteApp,
    scopeKey: resolveScopeKey({ isClient, id: idClienteApp }),
    isGroup: false,
  }
}
